import { useOrganization } from '@clerk/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { HoldChoice } from '@/types/custom-playbooks';
import {
  heldRecordsError,
  HOLD_KIND_LABELS,
  HoldDecisionCancelled,
  holdsAffectedCounts,
  type HeldRecords,
  type HoldCounts,
} from './held-records';

/** One captured edit, one explicit decision. Navigation and org/flag changes cancel it. */
export function useHeldRecordsDecision(
  scope: string,
  enabled = true,
  labels?: Record<string, string>
) {
  const { organization, isLoaded } = useOrganization();
  const { unlocked } = useAgentsAccess();
  const active = enabled && unlocked && !!isLoaded && !!organization;
  const key = `${organization?.id ?? ''}:${active}:${scope}`;
  const current = useRef({ key });
  if (current.current.key !== key) current.current = { key };
  const scopeToken = current.current;
  const pending = useRef<((choice: HoldChoice | null) => void) | null>(null);
  const tail = useRef<Promise<void>>(Promise.resolve());
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [decision, setDecision] = useState<
    (HeldRecords & { scopeToken: { key: string } }) | null
  >(null);
  useEffect(
    () => () => {
      pending.current?.(null);
      pending.current = null;
      tail.current = Promise.resolve();
    },
    [scopeToken]
  );
  const choose = useCallback((choice: HoldChoice | null) => {
    pending.current?.(choice);
    pending.current = null;
    setDecision(null);
  }, []);
  const run = useCallback(
    async <T,>(
      action: (holds?: HoldChoice) => Promise<T>,
      initial?: HoldCounts | (() => Promise<HeldRecords | undefined>)
    ): Promise<T> => {
      if (!active) return action();
      const execute = async () => {
        if (!mounted.current || current.current !== scopeToken)
          throw new HoldDecisionCancelled();
        let affected: HeldRecords | undefined =
          typeof initial === 'function'
            ? await initial()
            : initial && Object.keys(holdsAffectedCounts(initial)).length
              ? { counts: holdsAffectedCounts(initial) }
              : undefined;
        let choice: HoldChoice | undefined;
        for (;;) {
          if (!mounted.current || current.current !== scopeToken)
            throw new HoldDecisionCancelled();
          if (affected && active) {
            const answer = await new Promise<HoldChoice | null>((resolve) => {
              pending.current = resolve;
              setDecision({ ...affected!, scopeToken });
            });
            if (!answer || current.current !== scopeToken)
              throw new HoldDecisionCancelled();
            choice = answer;
          }
          try {
            const result = await action(choice);
            if (!mounted.current || current.current !== scopeToken)
              throw new HoldDecisionCancelled();
            return result;
          } catch (error) {
            affected = heldRecordsError(error);
            if (!affected || !active) throw error;
          }
        }
      };
      // Autosave can enqueue several edits while a decision is open. Keep every
      // edit in order, and give each one its own server-backed decision.
      const result = tail.current.then(execute);
      tail.current = result.then(
        () => undefined,
        () => undefined
      );
      return result;
    },
    [scopeToken, active]
  );
  const visible =
    active && decision?.scopeToken === scopeToken ? decision : null;
  const kindSummary = visible && 'waiting' in visible ? visible.waiting : null;
  const counts =
    visible && 'counts' in visible ? visible.counts : (kindSummary ?? {});
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
  const dialog = (
    <Dialog
      open={!!visible}
      onOpenChange={(open) => {
        if (!open) choose(null);
      }}
    >
      <DialogContent className='sm:max-w-[460px]' aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{total.toLocaleString()} records waiting</DialogTitle>
        </DialogHeader>
        <ul className='max-h-48 space-y-2 overflow-auto text-sm'>
          {Object.entries(counts)
            .filter(([, count]) => count > 0)
            .map(([id, count]) => (
              <li key={id} className='flex items-start justify-between gap-4'>
                <span className='min-w-0 break-words'>
                  {kindSummary
                    ? HOLD_KIND_LABELS[id as keyof typeof HOLD_KIND_LABELS]
                    : (labels?.[id] ?? id)}
                </span>
                <span className='shrink-0 tabular-nums'>
                  {count.toLocaleString()}
                </span>
              </li>
            ))}
        </ul>
        {visible?.message &&
          visible.message !==
            'Choose Keep at the next step or Drop for the waiting records' && (
            <p className='text-xs text-muted-foreground'>{visible.message}</p>
          )}
        <DialogFooter className='gap-2'>
          <Button variant='ghost' onClick={() => choose(null)}>
            Cancel
          </Button>
          <Button variant='destructive' onClick={() => choose('drop')}>
            Drop
          </Button>
          <Button onClick={() => choose('keep')}>
            {kindSummary ? 'Keep waiting' : 'Keep at next step'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
  return { run, dialog, pending: !!visible };
}
